import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Helmet } from 'react-helmet-async';
import { retrieveFromSessionStorage } from '../../util/helpers/sessionStorageHelper';
import { ACCOUNT_TYPE, ACCOUNT_TYPE_GTM } from '../../constants/sessionStorage';
import { useMenu } from '../../hooks/useMenu';
import { ACCOUNT_TYPES, AGREEMENT_TYPES } from '../../util/constants';
import Paragraph from '../../components/Paragraph/Paragraph';
import { ReactComponent as EquityTrustLogo } from '../../assets/images/svg/EquityTrustLogo.svg';
import PrintModal from '../../components/PrintModal/PrintModal';
import useStepPost from '../../hooks/useStepPost';
import useGtmHook from '../../util/hooks/useGtmHook';
import useApplicationUid from '../../hooks/useApplicationUid';
import {
  ACCOUNT_STEP_COMPLETE,
  ACCOUNT_STEP_VIEW,
  APPLICATION_TERMS_ACCEPTED,
} from '../../util/gtmEvents';
import { HeadingSubtitle } from '../../components/Heading';
import OnBoardingContainer from '../../components/OnBoarding/OnBoardingContainer';
import ChevronRight from '../../components/Icons/ChevronRight';
import { createAgreement } from '../../store/actions/agreement/agreementActions';
import { selectIsLoadingByActionType } from '../../store/selectors/loadingSelectors';
import { CREATE_AGREEMENT_LOADING } from '../../store/actions/agreement/agreementActionConstants';
import EquityAgreementDisplay from '../../components/Agreements/EquityAgreementDisplay';
import { selectSettings } from '../../store/selectors/settingsSelectors';
import themeColors from '../../assets/styles/themeColors';
import useGtmStepHook from '../../util/hooks/useGtmStepHook';
import { gtmStepHelper } from '../../util/helpers/gtmHelper';
import { useApplicantData } from '../../hooks/useApplicantData';

const EquityLogo = styled(EquityTrustLogo)`
  width: 143px;
  object-fit: contain;
  margin-bottom: 28px;
`;

const ViewTermsAndConditions = styled.button`
  display: flex;
  align-items: center;
  width: max-content;
  border: 2px solid red;
  border: none;
  background: transparent;
  cursor: pointer;
  text-decoration: underline;
`;

const EquityAgreement = () => {
  const accountType = retrieveFromSessionStorage(ACCOUNT_TYPE);
  const [isAgreementOpen, setIsAgreementOpen] = useState(false);
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { ApplicationUid } = useApplicationUid();
  const ApplicantData = useApplicantData();
  const { gtmEvent } = useGtmHook();
  const isLoading = useSelector(
    selectIsLoadingByActionType(CREATE_AGREEMENT_LOADING),
  );
  const settings = useSelector(selectSettings);
  const { BrokerName, EquityIraSetupFee } = settings;

  const { gtmStepEvent } = useGtmStepHook();
  const accountGtmType = retrieveFromSessionStorage(ACCOUNT_TYPE_GTM);

  useEffect(() => {
    if (
      ApplicantData?.PersonName &&
      ApplicantData?.PersonBirthday &&
      ApplicantData?.PersonAddress
    ) {
      gtmStepHelper({
        gtmStepEvent,
        eventName: ACCOUNT_STEP_VIEW,
        stepLabel: 'Agreement',
        accountType: accountGtmType,
        accountName: ApplicantData?.PersonName,
        accountDateOfBirth: ApplicantData?.PersonBirthday,
        accountPhone: ApplicantData?.Phone,
        accountAddress: ApplicantData?.PersonAddress,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleGtmEvent = () => {
    gtmStepHelper({
      gtmStepEvent,
      eventName: ACCOUNT_STEP_COMPLETE,
      stepLabel: 'Agreement',
      accountType: accountGtmType,
      accountName: ApplicantData?.PersonName,
      accountDateOfBirth: ApplicantData?.PersonBirthday,
      accountPhone: ApplicantData?.Phone,
      accountAddress: ApplicantData?.PersonAddress,
    });
  };

  const getTitle = () => {
    switch (accountType) {
      case ACCOUNT_TYPES.IRAApplication[1]:
        return t('equityAgreement.traditionalAccount');
      case ACCOUNT_TYPES.IRAApplication[2]:
        return t('equityAgreement.rothTitle');
      case ACCOUNT_TYPES.IRAApplication[3]:
        return t('equityAgreement.simpleTitle');
      case ACCOUNT_TYPES.IRAApplication[4]:
        return t('equityAgreement.sepTitle');

      default:
        return '';
    }
  };

  const title = getTitle();
  const { activeMenu, activeItem } = useMenu();

  const { onSuccess } = useStepPost({ nextPath: activeItem.nextPath });

  const onSubmit = () => {
    const onSuccessAPI = () => {
      gtmEvent(APPLICATION_TERMS_ACCEPTED);
      handleGtmEvent();
      onSuccess();
    };

    dispatch(
      createAgreement({
        ApplicationUid,
        type: AGREEMENT_TYPES.IRA,
        onSuccess: onSuccessAPI,
      }),
    );
  };

  return (
    <OnBoardingContainer
      activeMenu={activeMenu}
      activeItem={activeItem}
      previousPath={activeItem?.previousPath}
    >
      <Helmet>
        <title>
          {t('agreement.titleTagEquity', {
            brokerName: BrokerName,
          })}
        </title>
      </Helmet>
      <HeadingSubtitle mb={12} color={themeColors.colorTextSecondary}>
        {title}
      </HeadingSubtitle>
      <EquityLogo />
      <Paragraph mb={16} color={themeColors.colorTextXLLight}>
        {t('equityAgreement.howItWorks', {
          brokerName: BrokerName,
          equitySetupFee: EquityIraSetupFee,
        })}
      </Paragraph>
      <ViewTermsAndConditions
        onClick={() => setIsAgreementOpen(true)}
        data-cy="button-view-terms"
      >
        <Paragraph fontWeight={600}>{t('equityAgreement.tcView')}</Paragraph>
        <ChevronRight w="24px" h="24px" />
      </ViewTermsAndConditions>
      <Paragraph mt={16} color={themeColors.colorTextXLLight} variant="small">
        {t('equityAgreement.docusignNote')}
      </Paragraph>

      <PrintModal
        isOpen={isAgreementOpen}
        isEquityAgreement
        onClose={() => setIsAgreementOpen(false)}
        onCloseButton={() => onSubmit()}
        buttonText={t('continue')}
        variant="xl"
        hidePrint
        shouldScrollDown
        isLoading={isLoading}
      >
        <EquityAgreementDisplay type={AGREEMENT_TYPES.IRA} />
      </PrintModal>
    </OnBoardingContainer>
  );
};

export default EquityAgreement;
